import React, { useCallback, useEffect, useState } from 'react';
import { Copy, Check, Mail, Trash2, RefreshCw, AlertTriangle } from 'lucide-react';
import { createInvite, deleteInvite, getInvites, isApiError, type Invite } from '../services/api';

/**
 * Onglet « Invitations » du tableau de bord admin.
 *
 * Pourquoi : sur le VPS il n'y a PAS de SMTP. `POST /api/admin/invites` envoie bien un
 * email (perdu) mais l'interface n'exposait aucun lien — impossible de faire entrer la
 * promo autrement qu'en lisant les logs du serveur. Le backend renvoie désormais
 * `inviteUrl` (construit à partir de `FRONTEND_URL`, donc valable pour l'étudiant même
 * si l'admin travaille en local) et cet onglet le montre, copiable en un clic.
 */

const STATUS_LABELS: Record<Invite['status'], { label: string; className: string }> = {
  pending: {
    label: 'En attente',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
  },
  used: {
    label: 'Compte créé',
    className: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
  },
  expired: {
    label: 'Expirée',
    className: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  }
};

const formatDate = (value: string | null) => {
  if (!value) return '—';
  // Les dates SQLite sans fuseau sont de l'UTC naïf : on le rend explicite.
  const iso = /\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(value) ? `${value.replace(' ', 'T')}Z` : value;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * `navigator.clipboard` n'existe QUE dans un contexte sécurisé (https ou localhost) :
 * sur un VPS servi en http://<ip>, il est absent. D'où le repli `execCommand('copy')`.
 */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* on tente le repli */
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

export const CopyLinkButton: React.FC<{ url: string }> = ({ url }) => {
  const [state, setState] = useState<'idle' | 'done' | 'failed'>('idle');

  const handleCopy = async () => {
    const ok = await copyText(url);
    setState(ok ? 'done' : 'failed');
    window.setTimeout(() => setState('idle'), 2500);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title={url}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
    >
      {state === 'done' ? <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
      {state === 'done' ? 'Copié' : state === 'failed' ? 'Copie impossible' : 'Copier le lien'}
    </button>
  );
};

const AdminInvites: React.FC = () => {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [email, setEmail] = useState('');
  const [expiryHours, setExpiryHours] = useState('72');
  const [isCreating, setIsCreating] = useState(false);
  const [created, setCreated] = useState<{ email: string; inviteUrl: string; emailSent: boolean } | null>(null);

  const fetchInvites = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      setInvites(await getInvites());
    } catch (err) {
      setError(isApiError(err) && !err.isNetworkError
        ? err.message || 'Impossible de charger les invitations.'
        : 'Serveur injoignable.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setCreated(null);
    setIsCreating(true);
    try {
      setCreated(await createInvite({ email: email.trim(), expiryHours: Number(expiryHours) }));
      setEmail('');
      fetchInvites();
    } catch (err) {
      setError(isApiError(err) && !err.isNetworkError
        ? err.message || "Impossible de créer l'invitation."
        : 'Serveur injoignable.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (invite: Invite) => {
    setError('');
    try {
      await deleteInvite(invite.id);
      fetchInvites();
    } catch (err) {
      setError(isApiError(err) && !err.isNetworkError
        ? err.message || 'Suppression impossible.'
        : 'Serveur injoignable.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Création */}
      <div className="bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Inviter un étudiant</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          Un email est envoyé si un serveur SMTP est configuré. Sinon, copiez le lien ci-dessous et
          transmettez-le à l'étudiant (Discord, message, tableau…).
        </p>
        <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="prenom.nom@example.com"
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            disabled={isCreating}
          />
          <select
            value={expiryHours}
            onChange={(e) => setExpiryHours(e.target.value)}
            className="pl-3 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            disabled={isCreating}
          >
            <option value="24">Valable 24 h</option>
            <option value="72">Valable 3 jours</option>
            <option value="168">Valable 7 jours</option>
            <option value="720">Valable 30 jours</option>
          </select>
          <button
            type="submit"
            disabled={isCreating}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            <Mail className="w-4 h-4" />
            {isCreating ? 'Création...' : "Créer l'invitation"}
          </button>
        </form>

        {created && (
          <div className="mt-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
            <p className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">
              Invitation créée pour {created.email}
            </p>
            {!created.emailSent && (
              <p className="flex items-start gap-2 text-xs text-yellow-800 dark:text-yellow-200 mb-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                L'email n'a pas pu être envoyé (aucun SMTP configuré) : transmettez le lien vous-même.
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <input
                readOnly
                value={created.inviteUrl}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 px-2 py-1 text-xs font-mono border border-green-300 dark:border-green-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
              <CopyLinkButton url={created.inviteUrl} />
            </div>
          </div>
        )}

        {error && (
          <div className="mt-3 p-3 rounded bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Liste */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Invitations</h2>
          <button
            type="button"
            onClick={fetchInvites}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </button>
        </div>

        {isLoading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Chargement des invitations...</p>
        ) : invites.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Aucune invitation pour le moment.</p>
            <p className="text-sm">Créez-en une ci-dessus pour faire entrer un étudiant.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Statut</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Expire le</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Lien</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {invites.map((invite) => (
                  <tr key={invite.id}>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{invite.email}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_LABELS[invite.status].className}`}>
                        {STATUS_LABELS[invite.status].label}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(invite.expires_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {invite.inviteUrl ? (
                        <CopyLinkButton url={invite.inviteUrl} />
                      ) : (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {invite.status === 'used' ? 'Compte déjà créé' : 'Lien expiré'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {invite.status === 'used' ? (
                        <span className="text-xs text-gray-500 dark:text-gray-400">—</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleDelete(invite)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Supprimer
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInvites;
